import { useState, useRef, useEffect, useCallback } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useUserAuth } from '../../contexts/UserAuthContext';
import { useDiscoveryNav } from '../../hooks/useDiscoveryNav';
import { logoImage } from '../../utils/brandImages';
import styles from './TopBar.module.css';

function IconHome() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
      <polyline points="9 22 9 12 15 12 15 22" />
    </svg>
  );
}

function IconCompass() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="10" />
      <polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76" />
    </svg>
  );
}

function IconHeart() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
    </svg>
  );
}

function getInitials(input: string | null | undefined): string {
  if (!input) return '?';
  const trimmed = input.trim();
  if (!trimmed) return '?';
  // For email: take first 1-2 letters before @
  const local = (trimmed.includes('@') ? trimmed.split('@')[0] : trimmed) ?? '';
  if (!local) return '?';
  const parts = local.split(/[.\s_-]+/).filter(Boolean);
  const first = parts[0];
  const second = parts[1];
  if (first && second && first.length > 0 && second.length > 0) {
    return (first.charAt(0) + second.charAt(0)).toUpperCase();
  }
  return local.slice(0, 2).toUpperCase();
}

function UserMenu() {
  const { user, signOut } = useUserAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // T013: logout lands on the discovery Home of the current URL in v2, `/` in
  // legacy (the v2 entry gate would central-normalize a bare `/` anyway).
  const { homeUrl } = useDiscoveryNav();
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const handleLogout = useCallback(async () => {
    setOpen(false);
    try {
      await signOut();
    } finally {
      navigate(homeUrl, { replace: true });
    }
  }, [signOut, navigate, homeUrl]);

  if (!user) {
    // Use a button (not NavLink) so we can pass backgroundLocation state and
    // open AuthModal as overlay on desktop. On mobile (no AuthModal), the
    // state is harmless and the route renders as a full page.
    return (
      <button
        type="button"
        className={styles.navLink}
        onClick={() => navigate('/accedi', { state: { backgroundLocation: location } })}
      >
        <span>Accedi</span>
      </button>
    );
  }

  // Prefer display name if Supabase metadata has it, fall back to email
  const displayName =
    (user.user_metadata?.full_name as string | undefined) ||
    (user.user_metadata?.name as string | undefined) ||
    user.email ||
    '';
  const initials = getInitials(displayName || user.email);

  return (
    <div className={styles.userMenuWrapper} ref={wrapperRef}>
      <button
        type="button"
        className={styles.avatarButton}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Menu utente"
      >
        <span className={styles.avatar}>{initials}</span>
      </button>
      {open && (
        <div className={styles.dropdown} role="menu">
          <button
            type="button"
            className={styles.dropdownItem}
            onClick={() => {
              setOpen(false);
              navigate('/profilo');
            }}
            role="menuitem"
          >
            Profilo
          </button>
          <button
            type="button"
            className={styles.dropdownItem}
            onClick={handleLogout}
            role="menuitem"
          >
            Logout
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * v2 tab link (T013): a REAL `<a href>` whose target is the territorial URL,
 * so modified clicks / middle-click / new tabs work natively; a plain left
 * click is intercepted for SPA navigation. `aria-current` follows the
 * discovery surface (`nav.surface`), which recognizes territorial
 * Home/Esplora/category/map — NavLink's pathname matching cannot (a category
 * path is an Esplora surface but never matches the Esplora href).
 */
function NavAnchor({
  to,
  className,
  active,
  children,
  ...rest
}: {
  to: string;
  className: string | undefined;
  active?: boolean;
  'aria-label'?: string;
  children: React.ReactNode;
}) {
  const navigate = useNavigate();
  return (
    <a
      href={to}
      className={className}
      aria-current={active ? 'page' : undefined}
      onClick={(e) => {
        if (
          e.defaultPrevented ||
          e.button !== 0 ||
          e.metaKey ||
          e.ctrlKey ||
          e.shiftKey ||
          e.altKey
        ) {
          return; // modified click / middle click: the browser opens the anchor
        }
        e.preventDefault();
        navigate(to);
      }}
      {...rest}
    >
      {children}
    </a>
  );
}

// Il logo e alto 36 px (TopBar.module.css) e largo 36 * 483/170 = 102 px.
const TOPBAR_LOGO_SIZES = '102px';

export function TopBar() {
  // One class helper for both worlds: NavLink calls it with its own state
  // object, the v2 anchors call it with the discovery surface.
  const navLinkClass = ({ isActive }: { isActive: boolean }) =>
    isActive ? `${styles.navLink} ${styles.navLinkActive}` : styles.navLink;
  const nav = useDiscoveryNav();
  const logo = <img {...logoImage(TOPBAR_LOGO_SIZES)} alt="Buzz Event Map" />;

  return (
    <header className={styles.topbar}>
      {nav.v2 ? (
        <NavAnchor to={nav.homeUrl} className={styles.logo} aria-label="Buzz Event Map - Home">
          {logo}
        </NavAnchor>
      ) : (
        <NavLink to="/" className={styles.logo} aria-label="Buzz Event Map - Home">
          {logo}
        </NavLink>
      )}

      <nav className={styles.navLinks} aria-label="Navigazione principale">
        {nav.v2 ? (
          <>
            <NavAnchor
              to={nav.homeUrl}
              className={navLinkClass({ isActive: nav.surface === 'home' })}
              active={nav.surface === 'home'}
            >
              <IconHome />
              <span>Home</span>
            </NavAnchor>
            <NavAnchor
              to={nav.esploraUrl}
              className={navLinkClass({ isActive: nav.surface === 'esplora' })}
              active={nav.surface === 'esplora'}
            >
              <IconCompass />
              <span>Esplora</span>
            </NavAnchor>
          </>
        ) : (
          <>
            <NavLink to="/" end className={navLinkClass}>
              <IconHome />
              <span>Home</span>
            </NavLink>
            <NavLink to="/esplora" className={navLinkClass}>
              <IconCompass />
              <span>Esplora</span>
            </NavLink>
          </>
        )}
        <NavLink to="/salvati" className={navLinkClass}>
          <IconHeart />
          <span>Salvati</span>
        </NavLink>
      </nav>

      <div className={styles.spacer} />

      <UserMenu />
    </header>
  );
}
